// The merged grocery list for the recipes the user added from recipe pages.
import { useEffect, useMemo, useState } from 'preact/hooks';
import { api, ApiError } from '../api';
import { Link } from '../router';
import { Check } from '../icons';
import { BottomNav, ErrorBox, useToast } from '../components/Shell';
import { mergeQuantities } from '../lib/quantity';
import { groceryIDs, setGroceryIDs } from '../grocery-selection';
import { load, save, KEYS } from '../storage';
import type { GroceryItem, GroceryList } from '../types';
import { t as tr, useT } from '../i18n';
import { useScript } from '../script';
import { useProviders } from '../commerce';
import type { Script } from '../types';

const GROUPS: { key: GroceryItem['category']; title: () => string }[] = [
  { key: 'vegetables', title: () => tr('grocery.vegetables') },
  { key: 'spices', title: () => tr('grocery.spices') },
  { key: 'others', title: () => tr('grocery.others') },
];

const itemKey = (it: GroceryItem) => (it.name_translit || it.name_en).toLowerCase();

/** The item's name in the reader's script, and the other name to show under it. */
function itemNames(it: GroceryItem, script: Script): [string, string] {
  const main = (script === 'ta' ? it.name_ta : script === 'tg' ? it.name_translit : it.name_en) || it.name_en;
  const sub = script === 'ta' ? it.name_en : it.name_ta;
  return [main, sub && sub !== main ? sub : ''];
}

export function Grocery() {
  const t = useT();
  const [providers] = useProviders();
  const groceryApps = providers.filter((p) => p.kind === 'grocery');
  const [script] = useScript();
  const [ids, setIds] = useState<number[]>(groceryIDs);
  const [removed, setRemoved] = useState<number[]>([]);
  const [list, setList] = useState<GroceryList | null>(null);
  const [err, setErr] = useState('');
  const [checked, setChecked] = useState<Record<string, boolean>>(() => load(KEYS.groceryChecked, {}));
  const [hide, setHide] = useState<boolean>(() => load(KEYS.groceryHidePantry, false));
  const [toast, showToast] = useToast();

  useEffect(() => save(KEYS.groceryChecked, checked), [checked]);
  useEffect(() => save(KEYS.groceryHidePantry, hide), [hide]);
  useEffect(() => {
    setGroceryIDs(ids);
    if (ids.length === 0) { setList(null); return; }
    api.grocery(ids).then((l) => { setList(l); setErr(''); }).catch((e: ApiError) => {
      if (e.status === 404) { setIds([]); return; } // a recipe was removed from the library
      if (e.status !== 401) setErr(e.message);
    });
  }, [ids.join(',')]);

  const view = useMemo(() => {
    if (!list) return null;
    const hidden: string[] = [];
    const groups = GROUPS.map((g) => ({
      title: g.title(),
      items: list.items.filter((it) => {
        if (it.category !== g.key) return false;
        if (it.pantry && hide) { hidden.push(itemNames(it, script)[0].toLowerCase()); return false; }
        return true;
      }),
    })).filter((g) => g.items.length > 0);
    const total = groups.reduce((n, g) => n + g.items.length, 0);
    const ticked = groups.reduce((n, g) => n + g.items.filter((it) => checked[itemKey(it)]).length, 0);
    return { groups, hidden, total, ticked };
  }, [list, hide, checked, script]);

  const text = () => {
    if (!view) return '';
    const lines = [t('grocery.textTitle'), ''];
    view.groups.forEach((g) => {
      lines.push(g.title);
      g.items.forEach((it) => {
        const q = mergeQuantities(it.uses.map((u) => u.quantity));
        const [main, other] = itemNames(it, script);
        lines.push(`${checked[itemKey(it)] ? '✓' : '•'} ${main}${other ? ` (${other})` : ''}${q ? ` — ${q}` : ''}`);
      });
      lines.push('');
    });
    return lines.join('\n').trim();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(text()); showToast(t('grocery.copied')); } catch { showToast(t('grocery.copyFailed')); }
  };

  const recipes = list?.recipes ?? [];
  const removeRecipe = (id: number) => { setRemoved([...removed, id]); setIds(ids.filter((x) => x !== id)); };

  return (
    <div class="page with-nav" style="padding-bottom:calc(200px + env(safe-area-inset-bottom))">
      <div class="pad" style="padding-top:14px;display:flex;align-items:center;justify-content:space-between">
        <div style="display:flex;flex-direction:column">
          <h1 class="h1" style="font-size:28px">{t('grocery.title')}</h1>
          {view && <span class="muted" style="font-size:14px">{recipes.length === 1 ? t('grocery.summary_one', { n: view.total, b: view.ticked }) : t('grocery.summary', { n: view.total, b: view.ticked, r: recipes.length })}</span>}
        </div>
        <Link href="/albums" class="chip" style="height:44px;border-radius:12px">{t('grocery.addRecipes')}</Link>
      </div>

      {ids.length === 0 && (
        <div class="card" style="margin:16px;padding:20px;display:flex;flex-direction:column;gap:10px">
          <div class="display" style="font-weight:700;font-size:20px">{t('grocery.emptyTitle')}</div>
          <div class="muted" style="line-height:1.6">{t('grocery.emptyBody')}</div>
          <div class="small" style="color:var(--accent-text);font-weight:600">{t('grocery.swipeHint')}</div>
          {removed.length > 0 && <button class="btn btn-outline" onClick={() => { setIds(removed); setRemoved([]); }}>{t('grocery.bringBack', { n: removed.length })}</button>}
          <Link href="/albums" class="btn btn-primary">{t('grocery.goRecipes')}</Link>
        </div>
      )}

      {err && <ErrorBox message={err} onRetry={() => setIds([...ids])} />}

      {ids.length > 0 && (
        <>
          <div class="pad" style="padding-top:12px;display:flex;flex-wrap:wrap;gap:8px">
            {recipes.map((r) => (
              <span style="display:inline-flex;align-items:center;gap:2px;height:38px;padding:0 2px 0 12px;border-radius:999px;background:var(--soft);font-size:14px;font-weight:600">
                {r.dish}
                <button aria-label={t('grocery.remove', { dish: r.dish })} onClick={() => removeRecipe(r.reel_id)} style="width:36px;height:36px;border:0;background:transparent;font-size:18px">×</button>
              </span>
            ))}
            {removed.length > 0 && (
              <button class="chip" style="border:1.5px dashed var(--muted);background:transparent" onClick={() => { setIds([...ids, ...removed]); setRemoved([]); }}>
                {t('grocery.bringBack', { n: removed.length })}
              </button>
            )}
          </div>
          {list && list.skipped.length > 0 && <div class="pad small muted" style="padding-top:8px">{t('grocery.skipped', { n: list.skipped.length })}</div>}

          <button class="card" aria-pressed={hide} onClick={() => setHide(!hide)}
            style="margin:14px 16px 0;width:calc(100% - 32px);min-height:60px;padding:0 14px;border-radius:16px;display:flex;align-items:center;gap:12px;text-align:left">
            <span style="flex:1;display:flex;flex-direction:column;line-height:1.3">
              <span style="font-size:15px;font-weight:600">{t('grocery.hidePantry')}</span>
              <span class="small muted">{hide ? (view?.hidden.length ? t('grocery.hidden', { n: view.hidden.length, names: view.hidden.join(', ') }) : t('grocery.nothingHidden')) : t('grocery.showingBasics')}</span>
            </span>
            <span class={'switch' + (hide ? ' on' : '')} aria-hidden="true"><span /></span>
          </button>

          {!list && !err && <div class="skeleton" style="height:200px;margin:16px" aria-label={t('common.loading')} />}
          {view?.groups.map((g) => (
            <section style="padding:18px 16px 0">
              <h2 style="margin:0 0 8px 4px;font-size:13px;letter-spacing:.08em;text-transform:uppercase;color:var(--muted)">{g.title}</h2>
              <div class="check-list">
                {g.items.map((it) => {
                  const k = itemKey(it), on = !!checked[k];
                  const qty = mergeQuantities(it.uses.map((u) => u.quantity));
                  const nRecipes = new Set(it.uses.map((u) => u.reel_id)).size;
                  const [main, other] = itemNames(it, script);
                  const sub = [other, nRecipes > 1 ? t('grocery.nRecipes', { n: nRecipes }) : ''].filter(Boolean).join(' · ');
                  return (
                    <button class={'check-row' + (on ? ' done' : '')} style="min-height:58px" aria-pressed={on} onClick={() => setChecked({ ...checked, [k]: !on })}>
                      <span class="checkbox" style="width:24px;height:24px;border-radius:7px">{on && <Check size={15} style="color:var(--on-accent)" />}</span>
                      <span style="flex:1;min-width:0;display:flex;flex-direction:column">
                        <span class="check-name" style="font-size:16px" lang={script === 'ta' && it.name_ta ? 'ta' : undefined}>{main}</span>
                        {sub && <span class="check-sub" lang={script === 'ta' ? undefined : 'ta'}>{sub}</span>}
                      </span>
                      {qty ? <span class="check-qty">{qty}</span> : <span class="small muted" style="flex:none">{t('grocery.asNeeded')}</span>}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}

          <div style="position:fixed;left:0;right:0;bottom:calc(72px + env(safe-area-inset-bottom));z-index:5;background:var(--card);border-top:1px solid var(--line)">
            <div style="max-width:560px;margin:0 auto;padding:14px 16px 14px;display:flex;flex-direction:column;gap:10px">
              <div style="display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:10px">
                <button class="btn btn-outline" style="min-height:54px" onClick={copy} disabled={!view}>{t('grocery.copy')}</button>
                <a class="btn btn-primary" style="min-height:54px" href={`https://wa.me/?text=${encodeURIComponent(text())}`} target="_blank" rel="noopener">{t('grocery.whatsapp')}</a>
              </div>
              {groceryApps.length === 0
                ? <div style="height:40px;border-radius:12px;border:1px dashed var(--line);display:flex;align-items:center;justify-content:center;font-size:13px;color:var(--muted)">{t('grocery.later')}</div>
                : groceryApps.every((a) => a.coming_soon)
                  ? <div class="btn btn-dark is-soon" style="min-height:48px" aria-disabled="true">
                      {t('soon.orderOn', { apps: groceryApps.map((a) => a.name).join(' · ') })}<span class="soon-badge">{t('soon.badge')}</span>
                    </div>
                  : <Link href="/grocery/order" class="btn btn-dark" style="min-height:48px">{t('grocery.orderAll')}</Link>}
            </div>
          </div>
        </>
      )}
      {toast}
      <BottomNav current="grocery" />
    </div>
  );
}
